<template>
  <div
    v-if="!isFieldHidden"
    class="accounts-field"
    :class="{
      'accounts-field__extended': isFieldExtended,
      'accounts-field__required': showRequired
    }">
    <div
      v-if="fieldType !== 'component_edit' ? !fieldInstance.fieldData.is_hidden : true"
      class="accounts-field__item">
      <component
        :is="fieldInstance.field.component"
        v-bind="componentProps"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @search="search"
        @[fieldInstance.field.emit]="emit">
        <template #prefix>
          <div class="booster-accounts-offer__input-prefix">
            {{ fieldInstance.fieldData.placeholder }}
          </div>
        </template>
      </component>
    </div>

    <div
      v-if="fieldType !== 'component_edit' && dropdownRangeProps"
      class="accounts-field__item">
      <dropdown-range
        v-bind="dropdownRangeProps"
        @input="handleDropdownRangeInput" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'AccountsField',

  components: {
    UiInput: () => import('Components/ui/Input/UiInput.vue'),
    UiKitSelect: () => import('Components/ui/Select/UiKitSelect.vue'),
    MultipleSearchInput: () => import('Components/ui/Input/MultipleSearchInput.vue'),
    DropdownRange: () => import('Components/ui/Dropdown/DropdownRange.vue'),
  },

  props: {
    fieldInstance: {
      type: Object,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    hasError: {
      type: Boolean,
      default: false,
    },

    fieldType: {
      type: String,
      default: 'component_view',
      validate: (type) => ['component_view', 'component_edit'].includes(type),
    },
  },

  computed: {
    ...mapState(['userDevice']),

    componentProps() {
      return {
        ...this.fieldInstance.field.props,
        [this.fieldInstance.field.valueKey]: this.fieldInstance.field.value,
        disabled: this.isDisabled || this.fieldInstance.field.props.disabled,
        error: this.errorText,
        ...(this.fieldInstance.field.component === 'multiple-search-input'
          ? { 'always-show-results': true, 'should-clear-empty-value': true } : {}),
        // SKY-5994: For has_custom_value field type, allow to select only one value
        ...(this.fieldInstance.field.component === 'multiple-search-input'
          && this.fieldInstance.fieldData.has_custom_value
          ? { 'is-radio': true } : {}),
      };
    },

    errorText() {
      if (!this.hasError) {
        return false;
      }

      return this.fieldInstance.fieldData?.errorText ?? 'This field is required';
    },

    dropdownRangeProps() {
      if (!this.fieldInstance.fieldData.has_values_counter) {
        return null;
      }

      const maximumValue = this.fieldInstance.fieldData.product_currency_setting_field_values.length;
      const values = this.fieldInstance.fieldData.default_range_value
        ? this.fieldInstance.fieldData.default_range_value
        : [1, maximumValue];

      return {
        placeholder: this.fieldInstance.fieldData.component_view_data.values_counter_placeholder
                || `${this.fieldInstance.fieldData.placeholder}s count`,
        range: {
          values,
          borderValues: [1, maximumValue],
          step: 1,
          zones: [
            {
              from: 1,
              to: maximumValue,
              size: 100,
              step: 1,
            },
          ],
          scaleStepsCount: 1,
        },
      };
    },

    showRequired() {
      if (this.fieldType === 'component_edit' && !this.componentProps.value) {
        return this.fieldInstance.fieldData.is_required;
      }

      return null;
    },

    isFieldExtended() {
      return this.fieldType !== 'component_edit'
        && this.dropdownRangeProps
        && !this.fieldInstance.fieldData.is_hidden;
    },

    isFieldHidden() {
      return this.fieldInstance.fieldData.is_hidden
        && this.fieldType !== 'component_edit'
        && !this.dropdownRangeProps;
    },
  },

  methods: {
    emit(event) {
      if (this.fieldInstance.field.props.type === 'number') {
        event = Number(event);
      }

      if (JSON.stringify(this.fieldInstance.field.value) === JSON.stringify(event)) {
        return;
      }

      this.fieldInstance.setValue(event);
      this.$emit('input', {
        field: this.fieldInstance,
        value: event,
      });
    },

    search(event) {
      if (typeof this.fieldInstance.search === 'function') {
        this.fieldInstance.search(event);
      }
    },

    handleDropdownRangeInput(event) {
      this.$emit('input', {
        field: this.fieldInstance,
        range: event,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.accounts-field {
  --font-size: var(--field-font-size, 15px);
  --height: var(--field-height, 48px);

  display: contents;

  &__required {
    .accounts-field__item {
      position: relative;

      &::after {
        content: '*';
        position: absolute;
        top: -8px;
        right: -5px;
        color: $color--red;
        font-size: 16px;
        line-height: var(--height);
        padding-right: 10px;
        z-index: 100;
      }
    }
  }
}

.booster-accounts-offer__input-prefix {
  color: $color--basic-dark-gray;
}

.booster-accounts-offer__input-prefix {
  margin-right: 8px;
  padding-right: 8px;
  border-right: 1px solid $color--basic-dark-gray;
  font-weight: 500;
  font-size: 11px;
  letter-spacing: 0.55px;
  text-transform: uppercase;
  color: $color--basic-dark-gray;
}

.accounts-field__item {
  grid-column: 2 span;
  width: 100%;
  padding: 0 8px;
}

@media screen and #{$screen-more-or-1024} {
  .accounts-field__item {
    grid-column: 1 span;
  }
}

@media screen and #{$screen-more-or-1280} {
  .accounts-field__item {
    grid-column: 2 span;
  }
}
</style>
